import { useMemo, useState } from 'react';
import type { DocumentData, Timestamp } from 'firebase/firestore';
import { Markdown } from '../Markdown';
import { Loader } from '@/components/ui/loader';
import { Button, buttonClassName } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertBanner } from '@/components/AlertBanner';
import { labelClassName } from '@/components/ui/label';
import { Table, TableHead, TableBody, TableRow, TableHeadCell, TableCell } from '@/components/ui/table';
import { ChevronRight, AlertTriangle, FileJson, FileText, Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useT } from '@/lib/i18n';
import { callReadAuditAsset } from '@/lib/functions';
import { canViewModuleJson, canViewRawLog, fmtBytes, fmtDate, fmtMbps, rerunCommand } from '@/lib/utils';
import type { AuditModuleStatus, AuditRetention } from '@/types/models';
import type { messages as MSG } from '@/lib/i18n.messages';

// ─── Formes des contrats 1.0 et 1.1 lues par les « faits saillants » ────────
// Chaque section est conditionnée à la présence de sa clé : un module 1.0
// rend exactement les mêmes tableaux qu'avant, sans section vide.

interface DataEnvVar { name: string; kind: string; value: string }
interface DockerDataContainer { name: string; image?: string; data_env_vars?: DataEnvVar[] }
interface NetMount { source: string; mountpoint: string; fstype: string; options_allowlist?: string[]; kind?: string }
interface CronEntry { schedule: string; command_first_token: string }
interface ScheduledTask { task_name: string; next_run: string; status: string }
interface ZfsPool { free_bytes?: number; datasets_used_bytes?: number; counted?: boolean }
// 1.1
interface Virtualization { is_vm?: boolean; hypervisor?: string | null; evidence?: string | null }
interface SystemInfo { manufacturer?: string | null; model?: string | null; virtualization?: Virtualization }
interface DiskInfo { name?: string; model?: string | null; size_bytes?: number | null; media_type?: string | null; bus?: string | null }
interface CapacityBucket { total_bytes?: number; used_bytes?: number; free_bytes?: number }
interface HostedShare { path: string; name?: string | null; protocol?: string | null; clients_or_acl?: string | null }
interface NormalizedIface { name: string; mac?: string | null; ipv4?: string[]; ipv6?: string[]; state?: string | null; mtu?: number | null; speed_mbps?: number | null }
interface SystemdTimer { unit: string; activates?: string | null; next?: string | number | null; last?: string | number | null }
interface SystemdService { unit: string; active?: string; sub?: string; description?: string }
interface BackupTool { detected?: boolean; version?: string | null }

type NotSuccessStatus = Exclude<AuditModuleStatus, 'success'>;
const NOT_SUCCESS: readonly NotSuccessStatus[] = ['failed', 'skipped', 'interrupted'];
const isNotSuccess = (s: unknown): s is NotSuccessStatus => NOT_SUCCESS.includes(s as NotSuccessStatus);

export function ModuleTab({ moduleKey, module, auditId, retention }: { moduleKey: string; module?: DocumentData; auditId?: string; retention?: AuditRetention | null }) {
  const [view, setView] = useState<'report' | 'json' | 'raw'>('report');
  const { claims } = useAuth();
  const { t } = useT();
  const canJson = canViewModuleJson(claims.role);
  const canRaw = canViewRawLog(claims.role);

  if (!module) return <Loader label={t('audit.module.not_loaded')} />;

  const status: unknown = module.status;
  if (isNotSuccess(status)) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Badge variant={status === 'skipped' ? 'neutral' : 'danger'}>{t(`audit.module.status.${status}`)}</Badge>
          <span className="ml-auto text-xs text-ink-500">{moduleKey}</span>
        </div>
        <AlertBanner variant={status === 'skipped' ? 'info' : 'danger'} icon={<AlertTriangle className="h-4 w-4" />} title={t(`audit.module.status.${status}`)}>
          <div className="text-xs">
            {t('audit.module.failed.desc', { cmd: '' })}
            <code className="rounded bg-white/60 px-1.5 py-0.5 dark:bg-black/20">{rerunCommand([moduleKey])}</code>
          </div>
          {module.error && (
            <div className="mt-2 text-xs">
              <span className="font-medium">{t('audit.module.error_label')} :</span>{' '}
              <code className="break-all">{String(module.error)}</code>
            </div>
          )}
        </AlertBanner>
      </div>
    );
  }

  const effectiveView = (view === 'json' && canJson) || (view === 'raw' && canRaw) ? view : 'report';
  const viewButton = (id: 'report' | 'json' | 'raw', label: string) => (
    <button
      type="button"
      onClick={() => setView(id)}
      className={buttonClassName('ghost', effectiveView === id ? 'bg-slate-100 dark:bg-ink-700' : undefined)}
    >{label}</button>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {viewButton('report', t('audit.module.report'))}
        {canJson && viewButton('json', t('audit.module.json'))}
        {canRaw && viewButton('raw', t('audit.module.raw_log'))}
        <span className="ml-auto text-xs text-ink-500">{moduleKey}</span>
      </div>

      {effectiveView === 'report' && (
        <>
          <ModuleHighlights moduleKey={moduleKey} json={module.json} />
          <Card>
            <Markdown source={module.reportMd} />
          </Card>
        </>
      )}

      {effectiveView === 'json' && (
        <Card>
          <JsonView auditId={auditId} data={module.json} jsonPath={module.jsonPath as string | undefined} noDataLabel={t('audit.module.no_json')} />
        </Card>
      )}

      {effectiveView === 'raw' && (
        <Card>
          <RawLogView auditId={auditId} rawLogPath={module.rawLogPath as string | undefined} purgedAt={retention?.rawLogsPurgedAt ?? null} />
        </Card>
      )}
    </div>
  );
}

/** Chargement d'un actif Storage via readAuditAsset (journalisé côté fonctions). */
function useAssetLoader(auditId: string | undefined, path: string | undefined) {
  const [text, setText] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = async () => {
    if (!auditId || !path) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await callReadAuditAsset({ auditId, path });
      setText(data.text);
    } catch (e) {
      setError((e as Error).message);
    } finally { setLoading(false); }
  };
  return { text, loading, error, load };
}

function RawLogView({ auditId, rawLogPath, purgedAt }: { auditId?: string; rawLogPath?: string; purgedAt: Timestamp | null }) {
  const { t, locale } = useT();
  const { text, loading, error, load } = useAssetLoader(auditId, rawLogPath);
  if (purgedAt) return <div className="text-sm text-ink-500">{t('audit.module.raw_log.purged', { when: fmtDate(purgedAt, locale) })}</div>;
  if (!rawLogPath) return <div className="text-sm text-ink-500">{t('audit.module.raw_log.none')}</div>;
  if (text != null) return <pre className="max-h-[32rem] overflow-auto whitespace-pre-wrap break-all font-mono text-xs text-ink-800 dark:text-ink-100">{text}</pre>;
  return (
    <div className="space-y-2">
      <Button variant="secondary" onClick={load} disabled={loading}>
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} {t('audit.module.raw_log.load')}
      </Button>
      <p className="text-xs text-ink-500">{t('audit.module.asset.logged')}</p>
      {error && <p className="text-xs text-rose-600 dark:text-rose-300">{error}</p>}
    </div>
  );
}

// ─── Faits saillants (tableaux typés sur les formes 1.0) ────────────────────

export function ModuleHighlights({ moduleKey, json }: { moduleKey: string; json: unknown }) {
  const { t } = useT();
  if (!json || typeof json !== 'object') return null;
  const j = json as Record<string, unknown>;
  const sections: React.ReactNode[] = [];

  if (moduleKey === '01_hardware_compute') {
    // 1.1 : system{manufacturer,model,virtualization}, cpu.features, disks[].media_type
    const sys = (j.system ?? {}) as SystemInfo;
    const virt = sys.virtualization ?? {};
    if (sys.manufacturer || sys.model || typeof virt.is_vm === 'boolean') {
      sections.push(
        <Section key="system" title={t('audit.module.sec.system')}>
          <Facts rows={[
            sys.manufacturer ? { label: t('audit.module.tbl.manufacturer'), value: sys.manufacturer } : null,
            sys.model ? { label: t('audit.module.tbl.model'), value: sys.model } : null,
            typeof virt.is_vm === 'boolean'
              ? {
                label: t('audit.module.tbl.virtualization'),
                value: virt.is_vm
                  ? (virt.hypervisor ? t('audit.kpi.machine.vm', { hv: virt.hypervisor }) : t('audit.kpi.machine.vm_unknown'))
                  : t('audit.kpi.machine.physical'),
                hint: virt.evidence ?? undefined,
              }
              : null,
          ]} />
        </Section>,
      );
    }
    const features = ((j.cpu ?? {}) as { features?: string[] }).features ?? [];
    if (features.length > 0) {
      sections.push(
        <Section key="features" title={t('audit.module.sec.cpu_features')}>
          <div className="flex flex-wrap gap-1">
            {features.map(f => <code key={f} className="rounded bg-slate-100 px-1.5 py-0.5 text-xs dark:bg-ink-700">{f}</code>)}
          </div>
        </Section>,
      );
    }
    const disks = Array.isArray(j.disks) ? (j.disks as DiskInfo[]) : [];
    if (disks.length > 0) {
      sections.push(
        <Section key="disks" title={t('audit.module.sec.disks')}>
          <Table>
            <TableHead>
              <tr>
                <TableHeadCell>{t('audit.module.tbl.disk')}</TableHeadCell>
                <TableHeadCell>{t('audit.module.tbl.model')}</TableHeadCell>
                <TableHeadCell>{t('audit.module.tbl.media_type')}</TableHeadCell>
                <TableHeadCell>{t('audit.module.tbl.bus')}</TableHeadCell>
                <TableHeadCell>{t('audit.module.tbl.size')}</TableHeadCell>
              </tr>
            </TableHead>
            <TableBody>
              {disks.map((d, i) => (
                <TableRow key={`${d.name ?? ''}:${i}`}>
                  <TableCell><code className="text-xs">{d.name ?? '—'}</code></TableCell>
                  <TableCell>{d.model ?? '—'}</TableCell>
                  <TableCell>{d.media_type ? <Badge variant="neutral">{d.media_type.toUpperCase()}</Badge> : '—'}</TableCell>
                  <TableCell className="text-xs text-ink-500">{d.bus ?? '—'}</TableCell>
                  <TableCell>{fmtBytes(d.size_bytes ?? undefined)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>,
      );
    }
  }

  if (moduleKey === '02_compute_platforms') {
    const containers = ((j.docker_data_context as { containers?: DockerDataContainer[] } | undefined)?.containers ?? [])
      .filter(c => Array.isArray(c.data_env_vars) && c.data_env_vars.length > 0);
    if (containers.length > 0) {
      sections.push(
        <Section key="env" title={t('audit.module.sec.data_env_vars')}>
          <Table>
            <TableHead>
              <tr>
                <TableHeadCell>{t('audit.module.tbl.container')}</TableHeadCell>
                <TableHeadCell>{t('audit.module.tbl.variable')}</TableHeadCell>
                <TableHeadCell>{t('audit.module.tbl.kind')}</TableHeadCell>
                <TableHeadCell>{t('audit.module.tbl.value')}</TableHeadCell>
              </tr>
            </TableHead>
            <TableBody>
              {containers.flatMap(c => c.data_env_vars!.map(v => (
                <TableRow key={`${c.name}:${v.name}`}>
                  <TableCell>{c.name}<div className="text-xs text-ink-500">{c.image}</div></TableCell>
                  <TableCell><code className="text-xs">{v.name}</code></TableCell>
                  <TableCell><Badge variant={v.kind === 'secret' ? 'danger' : 'neutral'}>{v.kind}</Badge></TableCell>
                  <TableCell><code className="break-all text-xs">{v.value}</code></TableCell>
                </TableRow>
              )))}
            </TableBody>
          </Table>
        </Section>,
      );
    }
  }

  if (moduleKey === '03_storage_access') {
    // 1.1 : capacity_summary.local / .network / .largest_free_bytes
    const cap = (j.capacity_summary ?? {}) as { local?: CapacityBucket; network?: CapacityBucket; largest_free_bytes?: number; zfs_pools?: Record<string, ZfsPool> };
    if (cap.local || cap.network || typeof cap.largest_free_bytes === 'number') {
      sections.push(
        <Section key="capacity" title={t('audit.module.sec.capacity')}>
          <Table>
            <TableHead>
              <tr>
                <TableHeadCell>{t('audit.module.tbl.scope')}</TableHeadCell>
                <TableHeadCell>{t('audit.module.tbl.total')}</TableHeadCell>
                <TableHeadCell>{t('audit.module.tbl.used_total')}</TableHeadCell>
                <TableHeadCell>{t('audit.module.tbl.free')}</TableHeadCell>
              </tr>
            </TableHead>
            <TableBody>
              {([['local', cap.local], ['network', cap.network]] as const)
                .filter((entry): entry is readonly ['local' | 'network', CapacityBucket] => !!entry[1])
                .map(([key, bucket]) => (
                  <TableRow key={key}>
                    <TableCell>{t(key === 'local' ? 'audit.module.cap.local' : 'audit.module.cap.network')}</TableCell>
                    <TableCell>{fmtBytes(bucket.total_bytes)}</TableCell>
                    <TableCell>{fmtBytes(bucket.used_bytes)}</TableCell>
                    <TableCell>{fmtBytes(bucket.free_bytes)}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          {typeof cap.largest_free_bytes === 'number' && (
            <p className="mt-2 text-xs text-ink-500">{t('audit.module.cap.largest_free')} : {fmtBytes(cap.largest_free_bytes)}</p>
          )}
        </Section>,
      );
    }
    const shares = (j.shares ?? {}) as { mounted?: NetMount[]; hosted?: HostedShare[] };
    const nas = (j.nas_nfs ?? {}) as { nfs_mounts?: NetMount[]; smb_mounts?: NetMount[]; fstab_network_entries?: NetMount[] };
    // 1.1 : shares.mounted remplace nas_nfs.*_mounts (mêmes montages, avec `kind`).
    const mounted = shares.mounted ?? [];
    const mounts = mounted.length > 0 ? mounted : [...(nas.nfs_mounts ?? []), ...(nas.smb_mounts ?? [])];
    if (mounts.length > 0) {
      sections.push(<MountsTable key="mounts" title={t(mounted.length > 0 ? 'audit.module.sec.shares_mounted' : 'audit.module.sec.mounts')} mounts={mounts} />);
    }
    if ((shares.hosted ?? []).length > 0) {
      sections.push(<HostedSharesTable key="hosted" title={t('audit.module.sec.shares_hosted')} shares={shares.hosted!} />);
    }
    // Contrat 1.0 : capacity_summary.zfs_pools (module_03_storage_access.schema.json)
    const pools = cap.zfs_pools;
    if (pools && Object.keys(pools).length > 0) {
      sections.push(
        <Section key="zfs" title={t('audit.module.sec.zfs')}>
          <Table>
            <TableHead>
              <tr>
                <TableHeadCell>{t('audit.module.tbl.pool')}</TableHeadCell>
                <TableHeadCell>{t('audit.module.tbl.free')}</TableHeadCell>
                <TableHeadCell>{t('audit.module.tbl.used')}</TableHeadCell>
              </tr>
            </TableHead>
            <TableBody>
              {Object.entries(pools).map(([name, p]) => (
                <TableRow key={name}>
                  <TableCell><code className="text-xs">{name}</code></TableCell>
                  <TableCell>{fmtBytes(p.free_bytes)}</TableCell>
                  <TableCell>{fmtBytes(p.datasets_used_bytes)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>,
      );
    }
  }

  if (moduleKey === '04_network') {
    // 1.1 : interfaces.interfaces_normalized (fiable sous Windows)
    const ifaces = ((j.interfaces ?? {}) as { interfaces_normalized?: NormalizedIface[] }).interfaces_normalized ?? [];
    if (ifaces.length > 0) {
      sections.push(
        <Section key="ifaces" title={t('audit.module.sec.interfaces')}>
          <Table>
            <TableHead>
              <tr>
                <TableHeadCell>{t('audit.module.tbl.name')}</TableHeadCell>
                <TableHeadCell>{t('audit.module.tbl.mac')}</TableHeadCell>
                <TableHeadCell>{t('audit.module.tbl.ipv4')}</TableHeadCell>
                <TableHeadCell>{t('common.status')}</TableHeadCell>
                <TableHeadCell>{t('audit.module.tbl.mtu')}</TableHeadCell>
                <TableHeadCell>{t('audit.module.tbl.speed')}</TableHeadCell>
              </tr>
            </TableHead>
            <TableBody>
              {ifaces.map((iface, i) => (
                <TableRow key={`${iface.name}:${i}`}>
                  <TableCell><code className="text-xs">{iface.name}</code></TableCell>
                  <TableCell className="text-xs text-ink-500">{iface.mac || '—'}</TableCell>
                  <TableCell><code className="break-all text-xs">{(iface.ipv4 ?? []).join(', ') || '—'}</code></TableCell>
                  <TableCell>{iface.state ?? '—'}</TableCell>
                  <TableCell>{iface.mtu ?? '—'}</TableCell>
                  <TableCell>{fmtMbps(iface.speed_mbps)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>,
      );
    }
  }

  if (moduleKey === '05_data_sources') {
    const fileShares = (j.file_shares ?? {}) as { network_shares?: NetMount[]; mounted?: NetMount[]; hosted?: HostedShare[] };
    // 1.1 : file_shares.mounted / .hosted ; 1.0 : network_shares.
    const mounted = fileShares.mounted ?? [];
    const shares = mounted.length > 0 ? mounted : (fileShares.network_shares ?? []);
    if (shares.length > 0) {
      sections.push(<MountsTable key="shares" title={t(mounted.length > 0 ? 'audit.module.sec.shares_mounted' : 'audit.module.sec.mounts')} mounts={shares} />);
    }
    if ((fileShares.hosted ?? []).length > 0) {
      sections.push(<HostedSharesTable key="hosted" title={t('audit.module.sec.shares_hosted')} shares={fileShares.hosted!} />);
    }
  }

  if (moduleKey === '06_orchestration') {
    const cron = (j.cron_jobs ?? {}) as { user_crontab?: CronEntry[] | null; scheduled_tasks?: ScheduledTask[] };
    if (cron.user_crontab === null) {
      sections.push(<Section key="cron" title={t('audit.module.sec.crontab')}><p className="text-xs text-ink-500">{t('audit.module.crontab.unavailable')}</p></Section>);
    } else if (Array.isArray(cron.user_crontab) && cron.user_crontab.length > 0) {
      sections.push(
        <Section key="cron" title={t('audit.module.sec.crontab')}>
          <Table>
            <TableHead>
              <tr>
                <TableHeadCell>{t('audit.module.tbl.schedule')}</TableHeadCell>
                <TableHeadCell>{t('audit.module.tbl.command')}</TableHeadCell>
              </tr>
            </TableHead>
            <TableBody>
              {cron.user_crontab.map((c, i) => (
                <TableRow key={i}>
                  <TableCell><code className="text-xs">{c.schedule}</code></TableCell>
                  <TableCell><code className="break-all text-xs">{c.command_first_token}</code></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>,
      );
    }
    if (Array.isArray(cron.scheduled_tasks) && cron.scheduled_tasks.length > 0) {
      sections.push(
        <Section key="tasks" title={t('audit.module.sec.scheduled_tasks')}>
          <Table>
            <TableHead>
              <tr>
                <TableHeadCell>{t('audit.module.tbl.task')}</TableHeadCell>
                <TableHeadCell>{t('audit.module.tbl.next_run')}</TableHeadCell>
                <TableHeadCell>{t('common.status')}</TableHeadCell>
              </tr>
            </TableHead>
            <TableBody>
              {cron.scheduled_tasks.map((task, i) => (
                <TableRow key={i}>
                  <TableCell>{task.task_name}</TableCell>
                  <TableCell>{task.next_run}</TableCell>
                  <TableCell>{task.status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>,
      );
    }
  }

  if (moduleKey === '06_orchestration') {
    // 1.1 : systemd_services.timers, windows_services, backups.tools
    const systemd = (j.systemd_services ?? {}) as { services?: SystemdService[]; timers?: SystemdTimer[] };
    const timers = systemd.timers ?? [];
    if (timers.length > 0) {
      sections.push(
        <Section key="timers" title={t('audit.module.sec.timers')}>
          <Table>
            <TableHead>
              <tr>
                <TableHeadCell>{t('audit.module.tbl.unit')}</TableHeadCell>
                <TableHeadCell>{t('audit.module.tbl.activates')}</TableHeadCell>
                <TableHeadCell>{t('audit.module.tbl.next_run')}</TableHeadCell>
                <TableHeadCell>{t('audit.module.tbl.last_run')}</TableHeadCell>
              </tr>
            </TableHead>
            <TableBody>
              {timers.map((timer, i) => (
                <TableRow key={`${timer.unit}:${i}`}>
                  <TableCell><code className="text-xs">{timer.unit}</code></TableCell>
                  <TableCell><code className="text-xs">{timer.activates ?? '—'}</code></TableCell>
                  <TableCell className="text-xs">{timer.next ?? '—'}</TableCell>
                  <TableCell className="text-xs text-ink-500">{timer.last ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>,
      );
    }
    const services = systemd.services ?? [];
    const win = (j.windows_services ?? {}) as { available?: boolean; counts?: { running?: number; stopped?: number; other?: number } };
    if (services.length > 0 || win.available === true) {
      sections.push(
        <Section key="services" title={t('audit.module.sec.services')}>
          {win.available === true && (
            <p className="mb-2 text-xs text-ink-500">{t('audit.module.services.counts', {
              running: win.counts?.running ?? 0, stopped: win.counts?.stopped ?? 0, other: win.counts?.other ?? 0,
            })}</p>
          )}
          {services.length > 0 && (
            <Table>
              <TableHead>
                <tr>
                  <TableHeadCell>{t('audit.module.tbl.unit')}</TableHeadCell>
                  <TableHeadCell>{t('common.status')}</TableHeadCell>
                  <TableHeadCell>{t('audit.module.tbl.description')}</TableHeadCell>
                </tr>
              </TableHead>
              <TableBody>
                {services.map((svc, i) => (
                  <TableRow key={`${svc.unit}:${i}`}>
                    <TableCell><code className="text-xs">{svc.unit}</code></TableCell>
                    <TableCell>{[svc.active, svc.sub].filter(Boolean).join(' / ') || '—'}</TableCell>
                    <TableCell className="text-xs text-ink-500">{svc.description ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Section>,
      );
    }
    const tools = ((j.backups ?? {}) as { tools?: Record<string, BackupTool> }).tools;
    if (tools && Object.keys(tools).length > 0) {
      const detected = Object.entries(tools).filter(([, v]) => v?.detected === true);
      sections.push(
        <Section key="backups" title={t('audit.module.sec.backups')}>
          {detected.length === 0
            ? <p className="text-xs text-ink-500">{t('audit.module.backups.none')}</p>
            : (
              <div className="flex flex-wrap gap-1">
                {detected.map(([name, tool]) => (
                  <Badge key={name} variant="success">{name}{tool.version ? ` · ${tool.version}` : ''}</Badge>
                ))}
              </div>
            )}
        </Section>,
      );
    }
  }

  if (sections.length === 0) return null;
  return (
    <Card>
      <div className={labelClassName()}>{t('audit.module.highlights')}</div>
      <div className="mt-3 space-y-5">{sections}</div>
    </Card>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-2 text-sm font-medium text-ink-800 dark:text-ink-100">{title}</div>
      {children}
    </div>
  );
}

/** Liste label / valeur (faits d'un module, sans tableau). */
function Facts({ rows }: { rows: ({ label: string; value: string; hint?: string } | null)[] }) {
  const visible = rows.filter((r): r is { label: string; value: string; hint?: string } => !!r);
  return (
    <dl className="grid grid-cols-1 gap-1 text-sm sm:grid-cols-2">
      {visible.map(row => (
        <div key={row.label} className="flex gap-2">
          <dt className="text-ink-500">{row.label} :</dt>
          <dd className="text-ink-900 dark:text-white" title={row.hint}>{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function MountsTable({ title, mounts }: { title: string; mounts: NetMount[] }) {
  const { t } = useT();
  // `kind` n'existe qu'en 1.1 : la colonne n'apparaît que si au moins un montage le porte.
  const withKind = mounts.some(m => typeof m.kind === 'string');
  return (
    <Section title={title}>
      <Table>
        <TableHead>
          <tr>
            <TableHeadCell>{t('audit.module.tbl.source')}</TableHeadCell>
            <TableHeadCell>{t('audit.module.tbl.mountpoint')}</TableHeadCell>
            <TableHeadCell>{t('audit.module.tbl.fstype')}</TableHeadCell>
            {withKind && <TableHeadCell>{t('audit.module.tbl.kind_mount')}</TableHeadCell>}
            <TableHeadCell>{t('audit.module.tbl.options')}</TableHeadCell>
          </tr>
        </TableHead>
        <TableBody>
          {mounts.map((m, i) => (
            <TableRow key={`${m.mountpoint}:${i}`}>
              <TableCell><code className="break-all text-xs">{m.source}</code></TableCell>
              <TableCell><code className="text-xs">{m.mountpoint}</code></TableCell>
              <TableCell>{m.fstype}</TableCell>
              {withKind && <TableCell>{m.kind ? t(`audit.mount.kind.${m.kind}` as keyof typeof MSG) : '—'}</TableCell>}
              <TableCell className="text-xs text-ink-500">{(m.options_allowlist ?? []).join(', ')}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Section>
  );
}

/** Partages hébergés par la machine auditée (03/05 `shares.hosted`, 1.1). */
function HostedSharesTable({ title, shares }: { title: string; shares: HostedShare[] }) {
  const { t } = useT();
  return (
    <Section title={title}>
      <Table>
        <TableHead>
          <tr>
            <TableHeadCell>{t('audit.module.tbl.path')}</TableHeadCell>
            <TableHeadCell>{t('audit.module.tbl.name')}</TableHeadCell>
            <TableHeadCell>{t('audit.module.tbl.protocol')}</TableHeadCell>
            <TableHeadCell>{t('audit.module.tbl.clients')}</TableHeadCell>
          </tr>
        </TableHead>
        <TableBody>
          {shares.map((sh, i) => (
            <TableRow key={`${sh.path}:${i}`}>
              <TableCell><code className="break-all text-xs">{sh.path}</code></TableCell>
              <TableCell>{sh.name ?? '—'}</TableCell>
              <TableCell>{sh.protocol ? <Badge variant="neutral">{sh.protocol}</Badge> : '—'}</TableCell>
              <TableCell className="break-all text-xs text-ink-500">{sh.clients_or_acl ?? '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Section>
  );
}

function JsonView({ auditId, data, jsonPath, noDataLabel }: { auditId?: string; data: unknown; jsonPath?: string; noDataLabel: string }) {
  const { t } = useT();
  const { text, loading, error, load } = useAssetLoader(auditId, jsonPath);
  const parsed = useMemo(() => {
    if (text == null) return null;
    try { return { value: JSON.parse(text) as unknown }; } catch { return { value: text }; }
  }, [text]);
  const value = data ?? parsed?.value;
  if (value == null) {
    if (!jsonPath) return <div className="text-sm text-ink-500">{noDataLabel}</div>;
    return (
      <div className="space-y-2">
        <Button variant="secondary" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileJson className="h-4 w-4" />} {t('audit.module.json.load')}
        </Button>
        <p className="text-xs text-ink-500">{t('audit.module.asset.logged')}</p>
        {error && <p className="text-xs text-rose-600 dark:text-rose-300">{error}</p>}
      </div>
    );
  }
  return (
    <div className="font-mono text-xs">
      <Tree data={value} initialOpen depth={0} />
    </div>
  );
}

function Tree({ data, initialOpen = false, depth = 0 }: { data: unknown; initialOpen?: boolean; depth?: number }) {
  const [open, setOpen] = useState(initialOpen || depth < 2);
  if (data === null) return <span className="text-ink-400">null</span>;
  if (typeof data !== 'object') {
    return <span className={typeof data === 'string' ? 'text-emerald-600 dark:text-emerald-400' : 'text-brand-600 dark:text-brand-300'}>{JSON.stringify(data)}</span>;
  }
  const isArr = Array.isArray(data);
  const entries = Object.entries(data as Record<string, unknown>);
  if (entries.length === 0) return <span>{isArr ? '[]' : '{}'}</span>;

  return (
    <div>
      <button onClick={() => setOpen(o => !o)} className="inline-flex items-center gap-0.5 text-ink-500 hover:text-brand-500">
        <ChevronRight className={`h-3 w-3 transition ${open ? 'rotate-90' : ''}`} />
        <span>{isArr ? `[${entries.length}]` : `{${entries.length}}`}</span>
      </button>
      {open && (
        <ul className="mt-1 space-y-0.5 border-l border-slate-200 pl-3 dark:border-ink-700">
          {entries.map(([k, v]) => (
            <li key={k}>
              <span className="text-ink-700 dark:text-ink-200">{isArr ? '' : `"${k}": `}</span>
              <Tree data={v} depth={depth + 1} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
